import * as Tone from "tone";
import type { WASMFMSynth } from "~/lib/wasm/wasm-synth-wrapper";
import type { WASMMonoSynth } from "~/lib/wasm/wasm-mono-synth-wrapper";
import type { FilterBridge } from "~/lib/wasm/filter-bridge";
import type { EffectsBridge } from "~/lib/wasm/effects-bridge";

/**
 * MIDI CC to Synth Parameter Mapping
 *
 * Supports MonoSynth, FMSynth, WASM FM Synth, and WASM Mono Synth
 * Based on Moog Subsequent 37 MIDI implementation (MonoSynth) and FM synthesis parameters
 * See: docs/SYNTH_CC_MAPPING.md for full documentation
 */

export type SynthFilter = Tone.Filter | FilterBridge;
export type SupportedSynth = Tone.MonoSynth | Tone.FMSynth | WASMFMSynth | WASMMonoSynth;

/**
 * Scale a MIDI CC value (0-127) to a target range
 */
function scaleCCValue(ccValue: number, min: number, max: number, curve: 'linear' | 'exponential' = 'linear'): number {
  const normalized = ccValue / 127;

  if (curve === 'exponential') {
    // Use exponential curve for frequency and time values (sounds more natural)
    return min + (max - min) * Math.pow(normalized, 2);
  }

  // Linear scaling
  return min + (max - min) * normalized;
}

/**
 * Map MIDI CC to oscillator waveform type
 */
function ccToOscillatorType(ccValue: number): Tone.ToneOscillatorType {
  if (ccValue < 32) return "sine";
  if (ccValue < 64) return "square";
  if (ccValue < 96) return "sawtooth";
  return "triangle";
}

/**
 * Apply CC to MonoSynth (subtractive synthesis)
 */
function applyCCToMonoSynth(
  ccNumber: number,
  ccValue: number,
  synth: Tone.MonoSynth,
  filter: SynthFilter
): void {
  console.log(`🎛️ [CC MAPPING] Applying CC${ccNumber} = ${ccValue}`);

  switch (ccNumber) {
    // === STANDARD MIDI CCs ===

    case 1: // Modulation Wheel - affects filter cutoff
      {
        const modAmount = scaleCCValue(ccValue, 0, 5000, 'exponential');
        const currentFreq = filter.frequency.value as number;
        filter.frequency.value = Math.min(20000, currentFreq + modAmount);
        console.log(`🎛️ [CC1] Modulation → Filter cutoff +${modAmount.toFixed(0)}Hz`);
      }
      break;

    case 7: // Volume (Master)
      synth.volume.value = scaleCCValue(ccValue, -60, 0);
      console.log(`🎛️ [CC7] Volume → ${synth.volume.value.toFixed(1)}dB`);
      break;

    case 11: // Expression (Secondary Volume)
      synth.volume.value = scaleCCValue(ccValue, -60, 0);
      console.log(`🎛️ [CC11] Expression → ${synth.volume.value.toFixed(1)}dB`);
      break;

    case 74: // Filter Cutoff (Brightness)
      filter.frequency.value = scaleCCValue(ccValue, 200, 20000, 'exponential');
      console.log(`🎛️ [CC74] Filter Cutoff → ${filter.frequency.value.toFixed(0)}Hz`);
      break;

    case 71: // Filter Resonance (Timbre)
      filter.Q.value = scaleCCValue(ccValue, 0.1, 30, 'exponential');
      console.log(`🎛️ [CC71] Filter Resonance → ${filter.Q.value.toFixed(2)}`);
      break;

    // === AMP ENVELOPE CCs ===

    case 73: // Amp Attack
      synth.envelope.attack = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
      console.log(`🎛️ [CC73] Amp Attack → ${synth.envelope.attack.toFixed(3)}s`);
      break;

    case 75: // Amp Decay
      synth.envelope.decay = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
      console.log(`🎛️ [CC75] Amp Decay → ${synth.envelope.decay.toFixed(3)}s`);
      break;

    case 70: // Amp Sustain
      synth.envelope.sustain = scaleCCValue(ccValue, 0.0, 1.0);
      console.log(`🎛️ [CC70] Amp Sustain → ${synth.envelope.sustain.toFixed(2)}`);
      break;

    case 72: // Amp Release
      synth.envelope.release = scaleCCValue(ccValue, 0.001, 5.0, 'exponential');
      console.log(`🎛️ [CC72] Amp Release → ${synth.envelope.release.toFixed(3)}s`);
      break;

    // === FILTER ENVELOPE CCs ===

    case 16: // Filter Env Attack
      synth.filterEnvelope.attack = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
      console.log(`🎛️ [CC16] Filter Env Attack → ${synth.filterEnvelope.attack.toFixed(3)}s`);
      break;

    case 17: // Filter Env Decay
      synth.filterEnvelope.decay = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
      console.log(`🎛️ [CC17] Filter Env Decay → ${synth.filterEnvelope.decay.toFixed(3)}s`);
      break;

    case 18: // Filter Env Sustain
      synth.filterEnvelope.sustain = scaleCCValue(ccValue, 0.0, 1.0);
      console.log(`🎛️ [CC18] Filter Env Sustain → ${synth.filterEnvelope.sustain.toFixed(2)}`);
      break;

    case 19: // Filter Env Release
      synth.filterEnvelope.release = scaleCCValue(ccValue, 0.001, 5.0, 'exponential');
      console.log(`🎛️ [CC19] Filter Env Release → ${synth.filterEnvelope.release.toFixed(3)}s`);
      break;

    case 20: // Filter Env Amount (Octaves)
      synth.filterEnvelope.octaves = scaleCCValue(ccValue, 0, 7);
      console.log(`🎛️ [CC20] Filter Env Amount → ${synth.filterEnvelope.octaves.toFixed(1)} octaves`);
      break;

    case 21: // Filter Env Base Frequency
      synth.filterEnvelope.baseFrequency = scaleCCValue(ccValue, 50, 2000, 'exponential');
      console.log(`🎛️ [CC21] Filter Env Base → ${synth.filterEnvelope.baseFrequency.toFixed(0)}Hz`);
      break;

    // === OSCILLATOR CCs ===

    case 76: // Oscillator Type
      synth.oscillator.type = ccToOscillatorType(ccValue);
      console.log(`🎛️ [CC76] Oscillator Type → ${synth.oscillator.type}`);
      break;

    case 77: // Oscillator Detune
      synth.detune.value = scaleCCValue(ccValue, -100, 100);
      console.log(`🎛️ [CC77] Detune → ${synth.detune.value.toFixed(1)} cents`);
      break;

    case 78: // Portamento Time
      synth.portamento = scaleCCValue(ccValue, 0, 1.0, 'exponential');
      console.log(`🎛️ [CC78] Portamento → ${synth.portamento.toFixed(3)}s`);
      break;

    default:
      // Unmapped CC - log for future reference
      console.log(`🎛️ [CC${ccNumber}] Unmapped CC received (value: ${ccValue})`);
      break;
  }
}

/**
 * Apply CC to FMSynth (FM synthesis)
 */
function applyCCToFMSynth(
  ccNumber: number,
  ccValue: number,
  synth: Tone.FMSynth,
  _filter: SynthFilter
): void {
  console.log(`🎛️ [FM CC MAPPING] Applying CC${ccNumber} = ${ccValue}`);

  switch (ccNumber) {
    // === STANDARD MIDI CCs ===
    case 7: // Volume (Master)
      synth.volume.value = scaleCCValue(ccValue, -60, 0);
      console.log(`🎛️ [CC7] Volume → ${synth.volume.value.toFixed(1)}dB`);
      break;

    case 11: // Expression (Secondary Volume)
      synth.volume.value = scaleCCValue(ccValue, -60, 0);
      console.log(`🎛️ [CC11] Expression → ${synth.volume.value.toFixed(1)}dB`);
      break;

    // === FM SYNTHESIS PARAMETERS ===
    case 14: // Modulation Index
      synth.modulationIndex.value = scaleCCValue(ccValue, 0, 20);
      console.log(`🎛️ [CC14] Modulation Index → ${synth.modulationIndex.value.toFixed(2)}`);
      break;

    case 15: // Harmonicity
      synth.harmonicity.value = scaleCCValue(ccValue, 0.5, 4);
      console.log(`🎛️ [CC15] Harmonicity → ${synth.harmonicity.value.toFixed(2)}`);
      break;

    // === MODULATION ENVELOPE ===
    case 16: // Mod Env Attack
      synth.modulationEnvelope.attack = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
      console.log(`🎛️ [CC16] Mod Env Attack → ${synth.modulationEnvelope.attack.toFixed(3)}s`);
      break;

    case 17: // Mod Env Decay
      synth.modulationEnvelope.decay = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
      console.log(`🎛️ [CC17] Mod Env Decay → ${synth.modulationEnvelope.decay.toFixed(3)}s`);
      break;

    case 18: // Mod Env Sustain
      synth.modulationEnvelope.sustain = scaleCCValue(ccValue, 0.0, 1.0);
      console.log(`🎛️ [CC18] Mod Env Sustain → ${synth.modulationEnvelope.sustain.toFixed(2)}`);
      break;

    case 19: // Mod Env Release
      synth.modulationEnvelope.release = scaleCCValue(ccValue, 0.001, 5.0, 'exponential');
      console.log(`🎛️ [CC19] Mod Env Release → ${synth.modulationEnvelope.release.toFixed(3)}s`);
      break;

    // === CARRIER ENVELOPE ===
    case 73: // Carrier Attack
      synth.envelope.attack = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
      console.log(`🎛️ [CC73] Carrier Attack → ${synth.envelope.attack.toFixed(3)}s`);
      break;

    case 75: // Carrier Decay
      synth.envelope.decay = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
      console.log(`🎛️ [CC75] Carrier Decay → ${synth.envelope.decay.toFixed(3)}s`);
      break;

    case 70: // Carrier Sustain
      synth.envelope.sustain = scaleCCValue(ccValue, 0.0, 1.0);
      console.log(`🎛️ [CC70] Carrier Sustain → ${synth.envelope.sustain.toFixed(2)}`);
      break;

    case 72: // Carrier Release
      synth.envelope.release = scaleCCValue(ccValue, 0.001, 5.0, 'exponential');
      console.log(`🎛️ [CC72] Carrier Release → ${synth.envelope.release.toFixed(3)}s`);
      break;

    // === OSCILLATORS ===
    case 76: // Carrier Type
      synth.oscillator.type = ccToOscillatorType(ccValue);
      console.log(`🎛️ [CC76] Carrier Type → ${synth.oscillator.type}`);
      break;

    case 74: // Modulator Type
      synth.modulation.type = ccToOscillatorType(ccValue);
      console.log(`🎛️ [CC74] Modulator Type → ${synth.modulation.type}`);
      break;

    case 71: // Filter Resonance (Timbre)
      _filter.Q.value = scaleCCValue(ccValue, 0.1, 30, 'exponential');
      console.log(`🎛️ [CC71] Filter Resonance → ${_filter.Q.value.toFixed(2)}`);
      break;

    case 77: // Carrier Detune
      synth.detune.value = scaleCCValue(ccValue, -100, 100);
      console.log(`🎛️ [CC77] Detune → ${synth.detune.value.toFixed(1)} cents`);
      break;

    case 78: // Portamento Time
      synth.portamento = scaleCCValue(ccValue, 0, 1.0, 'exponential');
      console.log(`🎛️ [CC78] Portamento → ${synth.portamento.toFixed(3)}s`);
      break;

    default:
      console.log(`🎛️ [CC${ccNumber}] Unmapped FM CC received (value: ${ccValue})`);
      break;
  }
}

/**
 * Apply CC to WASM FM Synth
 */
function applyCCToWASMFMSynth(
  ccNumber: number,
  ccValue: number,
  synth: WASMFMSynth,
  _filter: SynthFilter
): void {
  console.log(`🎛️ [WASM FM CC MAPPING] Applying CC${ccNumber} = ${ccValue}`);

  const bridge = synth.wasmBridge;

  switch (ccNumber) {
    // === STANDARD MIDI CCs ===
    case 7: // Volume (Master)
      synth.volume.value = scaleCCValue(ccValue, -60, 0);
      console.log(`🎛️ [CC7] Volume → ${synth.volume.value.toFixed(1)}dB`);
      break;

    case 11: // Expression (Secondary Volume)
      synth.volume.value = scaleCCValue(ccValue, -60, 0);
      console.log(`🎛️ [CC11] Expression → ${synth.volume.value.toFixed(1)}dB`);
      break;

    // === FM SYNTHESIS PARAMETERS ===
    case 14: // Modulation Index
      {
        const value = scaleCCValue(ccValue, 0, 20);
        bridge.setModulationIndex(value);
        console.log(`🎛️ [CC14] Modulation Index → ${value.toFixed(2)}`);
      }
      break;

    case 15: // Harmonicity
      {
        const value = scaleCCValue(ccValue, 0.5, 4);
        bridge.setHarmonicity(value);
        console.log(`🎛️ [CC15] Harmonicity → ${value.toFixed(2)}`);
      }
      break;

    // === MODULATION ENVELOPE ===
    case 16: // Mod Env Attack
    case 17: // Mod Env Decay
    case 18: // Mod Env Sustain
    case 19: // Mod Env Release
      {
        // Get current envelope values and update the one that changed
        const attack = ccNumber === 16 ? scaleCCValue(ccValue, 0.001, 2.0, 'exponential') : 0.02;
        const decay = ccNumber === 17 ? scaleCCValue(ccValue, 0.001, 2.0, 'exponential') : 0.2;
        const sustain = ccNumber === 18 ? scaleCCValue(ccValue, 0.0, 1.0) : 0.4;
        const release = ccNumber === 19 ? scaleCCValue(ccValue, 0.001, 5.0, 'exponential') : 1.0;

        bridge.setModulationEnvelope(attack, decay, sustain, release);

        const paramName = ccNumber === 16 ? 'Attack' : ccNumber === 17 ? 'Decay' : ccNumber === 18 ? 'Sustain' : 'Release';
        const value = ccNumber === 16 ? attack : ccNumber === 17 ? decay : ccNumber === 18 ? sustain : release;
        console.log(`🎛️ [CC${ccNumber}] Mod Env ${paramName} → ${value.toFixed(3)}${ccNumber === 18 ? '' : 's'}`);
      }
      break;

    // === CARRIER ENVELOPE ===
    case 73: // Carrier Attack
    case 75: // Carrier Decay
    case 70: // Carrier Sustain
    case 72: // Carrier Release
      {
        // Get current envelope values and update the one that changed
        const attack = ccNumber === 73 ? scaleCCValue(ccValue, 0.001, 2.0, 'exponential') : 0.01;
        const decay = ccNumber === 75 ? scaleCCValue(ccValue, 0.001, 2.0, 'exponential') : 0.15;
        const sustain = ccNumber === 70 ? scaleCCValue(ccValue, 0.0, 1.0) : 0.4;
        const release = ccNumber === 72 ? scaleCCValue(ccValue, 0.001, 5.0, 'exponential') : 1.2;

        bridge.setCarrierEnvelope(attack, decay, sustain, release);

        const paramName = ccNumber === 73 ? 'Attack' : ccNumber === 75 ? 'Decay' : ccNumber === 70 ? 'Sustain' : 'Release';
        const value = ccNumber === 73 ? attack : ccNumber === 75 ? decay : ccNumber === 70 ? sustain : release;
        console.log(`🎛️ [CC${ccNumber}] Carrier ${paramName} → ${value.toFixed(3)}${ccNumber === 70 ? '' : 's'}`);
      }
      break;

    // === OSCILLATORS ===
    case 76: // Carrier Type (0=sine, 1=square, 2=saw, 3=triangle)
      {
        const oscType = ccValue < 32 ? 0 : ccValue < 64 ? 1 : ccValue < 96 ? 2 : 3;
        bridge.setCarrierType(oscType);
        const typeName = oscType === 0 ? 'sine' : oscType === 1 ? 'square' : oscType === 2 ? 'sawtooth' : 'triangle';
        console.log(`🎛️ [CC76] Carrier Type → ${typeName}`);
      }
      break;

    case 74: // Modulator Type
      {
        const oscType = ccValue < 32 ? 0 : ccValue < 64 ? 1 : ccValue < 96 ? 2 : 3;
        bridge.setModulatorType(oscType);
        const typeName = oscType === 0 ? 'sine' : oscType === 1 ? 'square' : oscType === 2 ? 'sawtooth' : 'triangle';
        console.log(`🎛️ [CC74] Modulator Type → ${typeName}`);
      }
      break;

    case 77: // Carrier Detune
      {
        const value = scaleCCValue(ccValue, -100, 100);
        synth.detune.value = value;
        console.log(`🎛️ [CC77] Detune → ${value.toFixed(1)} cents`);
      }
      break;

    case 71: // Filter Resonance (Timbre)
      {
        const resonance = scaleCCValue(ccValue, 0.1, 30, 'exponential');
        _filter.Q.value = resonance;
        console.log(`🎛️ [CC71] Filter Resonance → ${resonance.toFixed(2)}`);
      }
      break;

    case 78: // Portamento Time
      {
        const value = scaleCCValue(ccValue, 0, 1.0, 'exponential');
        bridge.setPortamento(value);
        console.log(`🎛️ [CC78] Portamento → ${value.toFixed(3)}s`);
      }
      break;

    default:
      console.log(`🎛️ [CC${ccNumber}] Unmapped WASM FM CC received (value: ${ccValue})`);
      break;
  }
}

/**
 * Apply CC to WASM Mono Synth
 */
function applyCCToWASMMonoSynth(
  ccNumber: number,
  ccValue: number,
  synth: WASMMonoSynth,
  filter: SynthFilter
): void {
  console.log(`🎛️ [WASM MONO CC MAPPING] Applying CC${ccNumber} = ${ccValue}`);

  switch (ccNumber) {
    // === STANDARD MIDI CCs ===
    case 7: // Volume (Master)
      synth.volume.value = scaleCCValue(ccValue, -60, 0);
      console.log(`🎛️ [CC7] Volume → ${synth.volume.value.toFixed(1)}dB`);
      break;

    case 11: // Expression (Secondary Volume)
      synth.volume.value = scaleCCValue(ccValue, -60, 0);
      console.log(`🎛️ [CC11] Expression → ${synth.volume.value.toFixed(1)}dB`);
      break;

    case 74: // Filter Cutoff
      {
        const cutoff = scaleCCValue(ccValue, 200, 20000, 'exponential');
        synth.setFilterCutoff(cutoff);
        // Also update FilterBridge if it's a WASM filter
        if ('setCutoff' in filter) {
          (filter as FilterBridge).setCutoff(cutoff);
        } else {
          filter.frequency.value = cutoff;
        }
        console.log(`🎛️ [CC74] Filter Cutoff → ${cutoff.toFixed(0)}Hz`);
      }
      break;

    case 71: // Filter Resonance
      {
        const resonance = scaleCCValue(ccValue, 0.5, 20, 'exponential');
        synth.setFilterResonance(resonance);
        // Also update FilterBridge if it's a WASM filter
        if ('setResonance' in filter) {
          (filter as FilterBridge).setResonance(resonance);
        } else {
          filter.Q.value = resonance;
        }
        console.log(`🎛️ [CC71] Filter Resonance → ${resonance.toFixed(2)}`);
      }
      break;

    // === AMP ENVELOPE CCs ===
    case 73: // Amp Attack
      {
        const attack = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
        // Store and apply full envelope (we need to track state for this)
        console.log(`🎛️ [CC73] Amp Attack → ${attack.toFixed(3)}s`);
      }
      break;

    case 75: // Amp Decay
      {
        const decay = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
        console.log(`🎛️ [CC75] Amp Decay → ${decay.toFixed(3)}s`);
      }
      break;

    case 70: // Amp Sustain
      {
        const sustain = scaleCCValue(ccValue, 0.0, 1.0);
        console.log(`🎛️ [CC70] Amp Sustain → ${sustain.toFixed(2)}`);
      }
      break;

    case 72: // Amp Release
      {
        const release = scaleCCValue(ccValue, 0.001, 5.0, 'exponential');
        console.log(`🎛️ [CC72] Amp Release → ${release.toFixed(3)}s`);
      }
      break;

    // === FILTER ENVELOPE CCs ===
    case 16: // Filter Env Attack
      {
        const attack = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
        console.log(`🎛️ [CC16] Filter Env Attack → ${attack.toFixed(3)}s`);
      }
      break;

    case 17: // Filter Env Decay
      {
        const decay = scaleCCValue(ccValue, 0.001, 2.0, 'exponential');
        console.log(`🎛️ [CC17] Filter Env Decay → ${decay.toFixed(3)}s`);
      }
      break;

    case 18: // Filter Env Sustain
      {
        const sustain = scaleCCValue(ccValue, 0.0, 1.0);
        console.log(`🎛️ [CC18] Filter Env Sustain → ${sustain.toFixed(2)}`);
      }
      break;

    case 19: // Filter Env Release
      {
        const release = scaleCCValue(ccValue, 0.001, 5.0, 'exponential');
        console.log(`🎛️ [CC19] Filter Env Release → ${release.toFixed(3)}s`);
      }
      break;

    case 20: // Filter Env Amount
      {
        const amount = scaleCCValue(ccValue, 0, 1);
        synth.setFilterEnvAmount(amount);
        console.log(`🎛️ [CC20] Filter Env Amount → ${amount.toFixed(2)}`);
      }
      break;

    // === OSCILLATOR CCs ===
    case 76: // Oscillator Type (0=sine, 1=square, 2=saw, 3=triangle)
      {
        const oscType = ccValue < 32 ? 0 : ccValue < 64 ? 1 : ccValue < 96 ? 2 : 3;
        synth.setOscillatorType(oscType);
        const typeName = oscType === 0 ? 'sine' : oscType === 1 ? 'square' : oscType === 2 ? 'sawtooth' : 'triangle';
        console.log(`🎛️ [CC76] Oscillator Type → ${typeName}`);
      }
      break;

    case 77: // Oscillator Detune
      {
        const detune = scaleCCValue(ccValue, -100, 100);
        synth.detune.value = detune;
        console.log(`🎛️ [CC77] Detune → ${detune.toFixed(1)} cents`);
      }
      break;

    case 78: // Portamento Time
      {
        const portamento = scaleCCValue(ccValue, 0, 1.0, 'exponential');
        synth.setPortamento(portamento);
        console.log(`🎛️ [CC78] Portamento → ${portamento.toFixed(3)}s`);
      }
      break;

    default:
      console.log(`🎛️ [CC${ccNumber}] Unmapped WASM Mono CC received (value: ${ccValue})`);
      break;
  }
}

/**
 * Apply CC to Effects Chain (Reverb, Compressor, Limiter)
 * CC 91-95 are standard effects controls
 */
export function applyCCToEffects(
  ccNumber: number,
  ccValue: number,
  effects: EffectsBridge
): void {
  console.log(`🎛️ [EFFECTS CC MAPPING] Applying CC${ccNumber} = ${ccValue}`);

  switch (ccNumber) {
    // === REVERB (CC 91) ===
    case 91: // Reverb Mix (standard)
      {
        const mix = scaleCCValue(ccValue, 0, 1);
        effects.setReverbMix(mix);
        console.log(`🎛️ [CC91] Reverb Mix → ${(mix * 100).toFixed(0)}%`);
      }
      break;

    case 92: // Reverb Decay
      {
        const decay = scaleCCValue(ccValue, 0, 1);
        effects.setReverbDecay(decay);
        console.log(`🎛️ [CC92] Reverb Decay → ${decay.toFixed(2)}`);
      }
      break;

    case 93: // Reverb Damping
      {
        const damping = scaleCCValue(ccValue, 0, 1);
        effects.setReverbDamping(damping);
        console.log(`🎛️ [CC93] Reverb Damping → ${damping.toFixed(2)}`);
      }
      break;

    // === COMPRESSOR (CC 94-95) ===
    case 94: // Compressor Threshold
      {
        const threshold = scaleCCValue(ccValue, -60, 0);
        effects.setCompressorThreshold(threshold);
        console.log(`🎛️ [CC94] Compressor Threshold → ${threshold.toFixed(0)}dB`);
      }
      break;

    case 95: // Compressor Ratio
      {
        const ratio = scaleCCValue(ccValue, 1, 20);
        effects.setCompressorRatio(ratio);
        console.log(`🎛️ [CC95] Compressor Ratio → ${ratio.toFixed(1)}:1`);
      }
      break;

    default:
      console.log(`🎛️ [CC${ccNumber}] Unmapped Effects CC received (value: ${ccValue})`);
      break;
  }
}

/**
 * Check if synth is a WASM FM Synth (has wasmBridge with setHarmonicity)
 */
function isWASMFMSynth(synth: unknown): synth is WASMFMSynth {
  return (
    synth !== null &&
    typeof synth === 'object' &&
    'isWASMSynth' in synth &&
    'wasmBridge' in synth &&
    typeof (synth as WASMFMSynth).wasmBridge?.setHarmonicity === 'function'
  );
}

/**
 * Check if synth is a WASM Mono Synth (has setOscillatorType but no setHarmonicity)
 */
function isWASMMonoSynth(synth: unknown): synth is WASMMonoSynth {
  return (
    synth !== null &&
    typeof synth === 'object' &&
    'isWASMSynth' in synth &&
    typeof (synth as WASMMonoSynth).setOscillatorType === 'function' &&
    typeof (synth as WASMMonoSynth).setFilterCutoff === 'function'
  );
}

/**
 * Unified CC application - dispatches to correct synth type
 */
export function applyCCToSynth(
  ccNumber: number,
  ccValue: number,
  synth: SupportedSynth,
  filter: SynthFilter
): void {
  // Check for WASM synths first (both have isWASMSynth method)
  if (isWASMFMSynth(synth)) {
    applyCCToWASMFMSynth(ccNumber, ccValue, synth, filter);
  } else if (isWASMMonoSynth(synth)) {
    applyCCToWASMMonoSynth(ccNumber, ccValue, synth, filter);
  } else if (synth instanceof Tone.FMSynth) {
    applyCCToFMSynth(ccNumber, ccValue, synth, filter);
  } else {
    applyCCToMonoSynth(ccNumber, ccValue, synth as Tone.MonoSynth, filter);
  }
}

/**
 * Apply multiple CC changes at once (e.g., from scramble)
 */
export function applyMultipleCCsToSynth(
  ccValues: Array<{ number: number; value: number; scramble: boolean }>,
  synth: SupportedSynth,
  filter: SynthFilter
): void {
  const scrambledCCs = ccValues.filter(cc => cc.scramble);

  console.log(`🎛️ [CC MAPPING] Applying ${scrambledCCs.length} CCs to synth`);

  scrambledCCs.forEach(cc => {
    applyCCToSynth(cc.number, cc.value, synth, filter);
  });

  console.log(`🎛️ [CC MAPPING] Complete`);
}

/**
 * Get the default/init CC values that map to the current synth state
 * These values come from the "Init" presets for both synths
 */
export function getSynthInitialCCValues(): Array<{ number: number; value: number; label: string }> {
  return [
    // Standard CCs
    { number: 1, value: 0, label: "Modulation" },
    { number: 7, value: 100, label: "Volume" },
    { number: 11, value: 100, label: "Expression" },

    // FM Synthesis (Init FM preset)
    { number: 14, value: 79, label: "Modulation Index" },
    { number: 15, value: 80, label: "Harmonicity" },

    // Monosynth Filter Envelope (Init Mono preset)
    { number: 16, value: 8, label: "Filter Env Attack" },
    { number: 17, value: 14, label: "Filter Env Decay" },
    { number: 18, value: 58, label: "Filter Env Sustain" },
    { number: 19, value: 96, label: "Filter Env Release" },
    { number: 20, value: 31, label: "Filter Env Amount" },
    { number: 21, value: 68, label: "Filter Env Base" },

    // Filter/Amp (Init FM preset - CC70, 72, 73, 74, 75, 76)
    { number: 70, value: 38, label: "Carrier Sustain (FM) / Amp Sustain (Mono)" },
    { number: 71, value: 76, label: "Filter Resonance (Mono)" },
    { number: 72, value: 39, label: "Carrier Release (FM) / Amp Release (Mono)" },
    { number: 73, value: 57, label: "Carrier Attack (FM) / Amp Attack (Mono)" },
    { number: 74, value: 101, label: "Modulator Type (FM) / Filter Cutoff (Mono)" },
    { number: 75, value: 31, label: "Carrier Decay (FM) / Amp Decay (Mono)" },
    { number: 76, value: 7, label: "Carrier Type (FM) / Osc Type (Mono)" },

    // Additional CCs
    { number: 77, value: 64, label: "Osc Detune" },
    { number: 78, value: 0, label: "Portamento" },

    // Effects CCs (CC 91-95)
    { number: 91, value: 38, label: "Reverb Mix" }, // ~30%
    { number: 92, value: 64, label: "Reverb Decay" }, // 50%
    { number: 93, value: 64, label: "Reverb Damping" }, // 50%
    { number: 94, value: 107, label: "Compressor Threshold" }, // ~-12dB
    { number: 95, value: 38, label: "Compressor Ratio" }, // ~4:1
  ];
}
